// Formatting helpers and label maps for the Product Support screen. These are
// the ONLY client-side derivations allowed — every number and state rendered by
// the panels comes verbatim from the API responses.

import { MoneyHealthStatus, MoneyHealthZone, SpendingHealthState } from "../../../services/SupportDebugService";

// 36-char hex/dash UUID (user IDs are UUIDs; org IDs are numeric).
export const UUID_REGEX =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export const EM_DASH = "—";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const formatCurrency = (value: number | null | undefined): string =>
  value == null ? EM_DASH : currencyFormatter.format(value);

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// "YYYY-MM-DD" -> "Aug 4, 2026". Parses the parts directly: new Date("YYYY-MM-DD")
// is treated as UTC midnight and shifts a day back in US timezones.
export const formatDate = (value: string | null | undefined): string => {
  if (!value) return EM_DASH;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return value;
  const month = MONTH_NAMES[parseInt(match[2], 10) - 1];
  if (!month) return value;
  return `${month} ${parseInt(match[3], 10)}, ${match[1]}`;
};

// Catalyst <Badge> colors are a fixed set of keys; restrict to the ones we use.
export type SupportDebugBadgeColor = "green" | "amber" | "orange" | "red" | "blue" | "zinc";

// Spending Health widget state -> label + badge color ("orange" is a valid
// Catalyst badge color, so slight_shortfall keeps its own tone).
export const SPENDING_HEALTH_STATE_DISPLAY: Record<
  SpendingHealthState,
  { label: string; color: SupportDebugBadgeColor }
> = {
  in_the_clear: { label: "In the clear", color: "green" },
  eating_into_goals: { label: "Eating into goals", color: "amber" },
  getting_tight: { label: "Getting tight", color: "amber" },
  slight_shortfall: { label: "Slight shortfall", color: "orange" },
  large_shortfall: { label: "Large shortfall", color: "red" },
};

export const spendingHealthStateDisplay = (
  state: string | undefined
): { label: string; color: SupportDebugBadgeColor } =>
  SPENDING_HEALTH_STATE_DISPLAY[state as SpendingHealthState] ?? {
    label: state ?? "Unknown",
    color: "zinc",
  };

// Money-health status (ENG-7822) -> label + badge color. Zones carry the
// tone: deep = red, slight = orange, marginal = amber, clear = green.
export const MONEY_HEALTH_STATUS_DISPLAY: Record<
  MoneyHealthStatus,
  { label: string; color: SupportDebugBadgeColor }
> = {
  committed_deep_shortfall: { label: "Committed — deep shortfall", color: "red" },
  committed_slight_shortfall: { label: "Committed — slight shortfall", color: "orange" },
  committed_marginal: { label: "Committed — marginal", color: "amber" },
  flexible_deep_pressure: { label: "Flexible — deep pressure", color: "red" },
  flexible_slight_pressure: { label: "Flexible — slight pressure", color: "orange" },
  flexible_marginal: { label: "Flexible — marginal", color: "amber" },
  goal_deep_pressure: { label: "Goal — deep pressure", color: "red" },
  goal_slight_pressure: { label: "Goal — slight pressure", color: "orange" },
  goal_marginal: { label: "Goal — marginal", color: "amber" },
  on_track: { label: "On track", color: "green" },
  no_goal: { label: "No goal", color: "green" },
};

export const moneyHealthStatusDisplay = (
  status: string | undefined
): { label: string; color: SupportDebugBadgeColor } =>
  MONEY_HEALTH_STATUS_DISPLAY[status as MoneyHealthStatus] ?? {
    label: status ?? "Unknown",
    color: "zinc",
  };

export const MONEY_HEALTH_ZONE_COLOR: Record<MoneyHealthZone, SupportDebugBadgeColor> = {
  deep: "red",
  slight: "orange",
  marginal: "amber",
  clear: "green",
};

export const moneyHealthZoneColor = (zone: string | undefined): SupportDebugBadgeColor =>
  MONEY_HEALTH_ZONE_COLOR[zone as MoneyHealthZone] ?? "zinc";

// Why the engine did / did not count a goal (goal_resolution) -> human text.
const GOAL_RESOLUTION_LABELS: Record<string, string> = {
  active: "Active goal counted",
  no_goal: "No goal set",
  expired: "Goal expired",
  completed: "Goal already reached — nothing left to fund",
  not_active: "Goal not active (abandoned or changed)",
  no_monthly_amount: "Goal has no monthly amount",
  invalid_monthly_amount: "Goal monthly amount is invalid",
  unavailable: "Goal service unavailable — treated as no goal",
};

export const goalResolutionLabel = (reason: string | null | undefined): string =>
  (reason && GOAL_RESOLUTION_LABELS[reason]) || reason || EM_DASH;

// 0.15 -> "15%"
export const formatPercent = (value: number | null | undefined): string =>
  value == null ? EM_DASH : `${(value * 100).toFixed(0)}%`;

// Layer ratio (position / size): -0.2133 -> "−21.3%"; null when the layer has no size.
export const formatRatio = (value: number | null | undefined): string =>
  value == null ? EM_DASH : `${(value * 100).toFixed(1)}%`.replace("-", "−");

// Widget eligibility skip reasons -> human text.
const ELIGIBILITY_SKIP_REASON_LABELS: Record<string, string> = {
  master_data_unavailable: "User profile service unavailable",
  missing_last_payday: "No last payday on file",
  no_declared_income: "No declared income",
  non_positive_projected_income: "Projected income is zero or negative",
  money_health_error: "Money Health Engine failed — see server logs",
  money_health_unavailable: "Money Health Engine returned no assessment",
};

const UNSUPPORTED_PAY_FREQUENCY_PREFIX = "unsupported_pay_frequency:";

export const eligibilitySkipReasonLabel = (reason: string | null | undefined): string => {
  if (!reason) return "Unknown";
  if (reason.startsWith(UNSUPPORTED_PAY_FREQUENCY_PREFIX)) {
    const frequency = reason.slice(UNSUPPORTED_PAY_FREQUENCY_PREFIX.length) || "unknown";
    return `Pay frequency not supported: ${frequency}`;
  }
  return ELIGIBILITY_SKIP_REASON_LABELS[reason] ?? reason;
};

// Per-category projection skip reasons -> short note for the table.
const CATEGORY_SKIP_NOTES: Record<string, string> = {
  day_one_no_history: `Day 1 ${EM_DASH} no burn history`,
  no_spend_in_period: "No spend yet",
  limit_consumed_by_bills: "Budget fully covered by bills",
  no_history: "No spending history",
  nothing_remaining: "Budget already used up",
};

export const categorySkipNote = (reason: string | null | undefined): string => {
  if (!reason) return EM_DASH;
  return CATEGORY_SKIP_NOTES[reason] ?? reason;
};

// User status pill colors on the lookup card / result rows.
export const userStatusBadgeColor = (status: string | null | undefined): SupportDebugBadgeColor => {
  switch ((status ?? "").toUpperCase()) {
    case "ACTIVE":
      return "green";
    case "INVITED":
    case "PENDING":
      return "amber";
    case "DEACTIVATED":
    case "INACTIVE":
      return "red";
    default:
      return "zinc";
  }
};
